import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../services/axios';
import analyticsService from '../services/analytics';

const AuthContext = createContext(null);

// Utility to extract error message safely
function getErrorMessage(error) {
  if (!error) return 'Unknown error';
  if (typeof error === 'string') return error;
  if (error instanceof Error) return error.message;
  if (error.message) return error.message;
  if (error.type && error.type === 'error' && error.target) return 'A network error occurred.';
  // Handle Event objects specifically
  if (error instanceof Event) return 'An unexpected event occurred.';
  if (typeof error.toString === 'function') return error.toString();
  return 'Unknown error';
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Wrapper function for setUser with debugging
  const setUserWithDebug = (userData) => {
    console.log('AuthContext: setUserWithDebug called with:', userData ? userData.email : 'null');
    setUser(userData);
  };

  const navigate = useNavigate();
  const location = useLocation();
  const sessionCheckRef = useRef(null);
  const lastSessionCheckRef = useRef(0);

  const handleAuthError = useCallback((error) => {
    console.error('Auth error:', error);
    // Handle 429 errors differently - don't redirect, just show error
    if (error.response?.status === 429) {
      setError('Too many requests. Please wait a moment and try again.');
      return;
    }
    const message = error.response?.data?.message || getErrorMessage(error) || 'Authentication failed';
    setError(message);
    setUser(null);
    // Only redirect to login if not already on login page and not a 429 error
    if (location.pathname !== '/login' && location.pathname !== '/team-login' && error.response?.status !== 429) {
      // Check if we're on a team-related page and redirect accordingly
      if (location.pathname.startsWith('/team-')) {
        navigate('/team-login');
      } else {
        navigate('/login');
      }
    }
  }, [navigate, location.pathname, setError, setUser]);

  const signup = async (signupData) => {
    try {
      setError(null);
      
      const response = await api.post('/auth/register', signupData);
      const userData = response.data.data.user;
      
      setUserWithDebug(userData);
      
      // Identify user in Mixpanel
      analyticsService.identifyUser(userData.id, {
        email: userData.email,
        name: userData.name,
        plan: userData.plan || 'free',
        signup_date: new Date().toISOString(),
        company: userData.company?.name || null
      });
      
      // Track signup conversion
      analyticsService.trackConversion('signup_completed', {
        plan: userData.plan || 'free',
        company: userData.company?.name || null
      });
      
      navigate('/app');
      return true;
    } catch (error) {
      handleAuthError(error);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      setError(null);
      
      console.log('AuthContext: Login attempt for:', email);
      const response = await api.post('/auth/login', { email, password });
      const userData = response.data.data.user;
      
      console.log('AuthContext: Login successful, user data:', userData);
      console.log('AuthContext: Setting user in state:', userData.email);
      setUserWithDebug(userData);
      
      // Identify user in Mixpanel
      analyticsService.identifyUser(userData.id, {
        email: userData.email,
        name: userData.name,
        plan: userData.plan || 'free',
        last_login: new Date().toISOString(),
        company: userData.company?.name || null
      });
      
      // Track login event
      analyticsService.trackEvent('User Logged In', {
        plan: userData.plan || 'free',
        company: userData.company?.name || null
      });
      
      navigate('/app');
      return true;
    } catch (error) {
      console.error('AuthContext: Login error:', error);
      handleAuthError(error);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      // Track logout event before clearing user data
      if (user) {
        analyticsService.trackEvent('User Logged Out', {
          plan: user.plan || 'free',
          company: user.company?.name || null
        });
      }
      
      // Call the server's logout endpoint
      await api.post('/auth/logout');
    } catch (error) {
      console.error("Logout error (server):", error);
    } finally {
      // Clear user data (state only)
      setUserWithDebug(null);
      
      // Reset Mixpanel user
      analyticsService.resetUser();
      
      // Use React Router navigate instead of window.location.href
      // This prevents full page reload and cookie consent banner
      navigate('/');
    }
  };

  // Check session status on mount only
  useEffect(() => {
    const checkSession = async () => {
      // List of public routes that don't require auth
      const publicRoutes = ['/', '/features', '/pricing', '/resources', '/resources/:s3Key(*)', '/about', '/contact', '/waitlist', '/privacy', '/terms', '/signup', '/login', '/team-signup', '/team-login', '/account-linking', '/join-team', '/forgot-password'];
      
      // Skip session check on public pages
      if (publicRoutes.includes(location.pathname) || location.pathname.startsWith('/resources/') || location.pathname.startsWith('/reset-password/')) {
        setLoading(false);
        return;
      }

      // Prevent multiple simultaneous session checks
      if (sessionCheckRef.current) {
        return;
      }

      // Add debounce to prevent rapid requests (minimum 5 seconds between checks)
      const now = Date.now();
      if (now - lastSessionCheckRef.current < 5000) {
        setLoading(false);
        return;
      }

      try {
        sessionCheckRef.current = true;
        lastSessionCheckRef.current = now;
        
        // Verify session with server
        const response = await api.get('/auth/me');
        const userData = response.data.data.user;
        setUserWithDebug(userData);
        
        // Identify user in Mixpanel for session restoration
        analyticsService.identifyUser(userData.id, {
          email: userData.email,
          name: userData.name,
          plan: userData.plan || 'free',
          last_session_restore: new Date().toISOString(),
          company: userData.company?.name || null
        });
      } catch (error) {
        console.error('Session check failed:', error);
        
        // For 429 errors, keep the user logged in if we have stored data
        if (error.response?.status === 429) {
          const storedUser = localStorage.getItem('user');
          if (storedUser) {
            setUserWithDebug(JSON.parse(storedUser));
            setError('Session verification temporarily unavailable. Please try again later.');
          // Only handle auth errors if we don't have a user
          } else if (!user) {
            handleAuthError(error);
          }
        }
      } finally {
        setLoading(false);
        sessionCheckRef.current = null;
      }
    };

    checkSession();
  }, [handleAuthError]);

  // Session activity tracking is handled by the server

  const verifyOTP = async (otp, sessionId) => {
    try {
      setError(null);
      setLoading(true);
      
      const response = await api.post('/auth/verify-otp', { otp, sessionId });
      const userData = response.data.data.user;
      
      setUserWithDebug(userData);
      
      // Identify user in Mixpanel
      analyticsService.identifyUser(userData.id, {
        email: userData.email,
        name: userData.name,
        plan: userData.plan || 'free',
        last_login: new Date().toISOString(),
        company: userData.company?.name || null
      });
      
      // Track login event
      analyticsService.trackEvent('User Logged In', {
        plan: userData.plan || 'free',
        company: userData.company?.name || null,
        twoFactorEnabled: true
      });
      
      navigate('/app');
      return true;
    } catch (error) {
      console.error('AuthContext: OTP verification error:', error);
      handleAuthError(error);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const resendOTP = async (sessionId) => {
    try {
      setError(null);
      const response = await api.post('/auth/resend-otp', { sessionId });
      return response.data;
    } catch (error) {
      console.error('AuthContext: Resend OTP error:', error);
      handleAuthError(error);
      throw error;
    }
  };

  const value = {
    user,
    loading,
    error,
    login,
    signup,
    logout,
    verifyOTP,
    resendOTP,
    setError,
    setUser,
    api,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
